/**
 * Job Service
 * Handles job tracking operations for waste management projects
 */

import { apiClient } from './api';
import {
  ensureJobResponse,
  ensureJobStatsResponse,
  ensurePermitsResponse,
  ensurePermit,
  ensureMaterialTicketsResponse,
  ensureMaterialTicketResponse,
} from '@/lib/api/type-guards';

// Job types
export type JobStatus = 'active' | 'complete';
export type PermitType = 'building' | 'demolition' | 'grading' | 'other';
export type DestinationType = 'recycling' | 'landfill' | 'donation' | 'composting' | 'salvage';

export interface Job {
  id: string;
  plan_id: string;
  status: JobStatus;
  project_name: string;
  project_address: string;
  start_date: number;
  target_completion_date?: number;
  target_diversion_rate: number;
  created_at: number;
  updated_at: number;
  last_accessed_at: number;
}

export interface Permit {
  id: string;
  job_id: string;
  permit_number: string;
  permit_type: PermitType;
  issuing_jurisdiction: string;
  issue_date?: number;
  expiration_date?: number;
  created_at: number;
}

export interface TicketMaterial {
  material_type: string;
  weight_tons: number;
  is_diverted: boolean;
}

export interface MaterialTicket {
  id: string;
  job_id: string;
  ticket_number?: string;
  ticket_date: number;
  hauler_name: string;
  facility_id?: string;
  facility_name: string;
  destination_type: DestinationType;
  notes?: string;
  materials: TicketMaterial[];
  created_at: number;
  updated_at: number;
}

export interface MaterialSummary {
  material_type: string;
  total_weight: number;
  diverted_weight: number;
  landfill_weight: number;
  diversion_rate: number;
}

export interface JobStats {
  total_weight: number;
  diverted_weight: number;
  landfill_weight: number;
  diversion_rate: number;
  ticket_count: number;
  material_summary: MaterialSummary[];
}

// Request types
export interface CreateJobRequest {
  plan_id: string;
  project_name: string;
  project_address: string;
  start_date: number;
  target_completion_date?: number;
  target_diversion_rate?: number;
}

export interface CreatePermitRequest {
  permit_number: string;
  permit_type: PermitType;
  issuing_jurisdiction: string;
  issue_date?: number;
  expiration_date?: number;
}

export interface CreateMaterialTicketRequest {
  ticket_number?: string;
  ticket_date: number;
  hauler_name: string;
  facility_id?: string;
  facility_name: string;
  destination_type: DestinationType;
  notes?: string;
  materials: Array<{
    material_type: string;
    weight_tons: number;
    is_diverted: boolean;
  }>;
}

export interface UpdateJobRequest {
  status?: JobStatus;
  target_completion_date?: number;
}

// Response types
interface JobResponse {
  job: Job;
}

interface JobStatsResponse {
  stats: JobStats;
}

interface PermitsResponse {
  permits: Permit[];
}

interface MaterialTicketsResponse {
  tickets: MaterialTicket[];
}

export const jobService = {
  /**
   * Get job by ID
   */
  async getJob(jobId: string): Promise<Job> {
    const response = await apiClient.get<JobResponse>(`/jobs/${jobId}`);
    return ensureJobResponse(response).job;
  },

  /**
   * Get job by plan ID
   */
  async getJobByPlanId(planId: string): Promise<Job | null> {
    try {
      const response = await apiClient.get<JobResponse>(`/plans/${planId}/job`);
      return ensureJobResponse(response).job;
    } catch (error) {
      // Return null if job doesn't exist (404)
      if (error && typeof error === 'object' && 'statusCode' in error && error.statusCode === 404) {
        return null;
      }
      throw error;
    }
  },

  /**
   * Create a new job for a plan
   */
  async createJob(data: CreateJobRequest): Promise<Job> {
    const response = await apiClient.post<JobResponse>('/jobs', data);
    return ensureJobResponse(response).job;
  },

  /**
   * Update job
   */
  async updateJob(jobId: string, data: UpdateJobRequest): Promise<Job> {
    const response = await apiClient.patch<JobResponse>(`/jobs/${jobId}`, data);
    return ensureJobResponse(response).job;
  },

  /**
   * Get job statistics (diversion rate, totals, etc.)
   */
  async getJobStats(jobId: string): Promise<JobStats> {
    const response = await apiClient.get<JobStatsResponse>(`/jobs/${jobId}/stats`);
    return ensureJobStatsResponse(response).stats;
  },

  /**
   * Get permits for a job
   */
  async getPermits(jobId: string): Promise<Permit[]> {
    const response = await apiClient.get<PermitsResponse>(`/jobs/${jobId}/permits`);
    return ensurePermitsResponse(response).permits;
  },

  /**
   * Create a permit
   */
  async createPermit(jobId: string, data: CreatePermitRequest): Promise<Permit> {
    const response = await apiClient.post<{ permit: Permit }>(`/jobs/${jobId}/permits`, data);
    return ensurePermit(response.permit);
  },

  /**
   * Delete a permit
   */
  async deletePermit(jobId: string, permitId: string): Promise<void> {
    await apiClient.delete(`/jobs/${jobId}/permits/${permitId}`);
  },

  /**
   * Get material tickets for a job
   */
  async getMaterialTickets(jobId: string): Promise<MaterialTicket[]> {
    const response = await apiClient.get<MaterialTicketsResponse>(`/jobs/${jobId}/tickets`);
    return ensureMaterialTicketsResponse(response).tickets;
  },

  /**
   * Create a material ticket
   */
  async createMaterialTicket(jobId: string, data: CreateMaterialTicketRequest): Promise<MaterialTicket> {
    const response = await apiClient.post<{ ticket: MaterialTicket }>(`/jobs/${jobId}/tickets`, data);
    return ensureMaterialTicketResponse(response).ticket;
  },

  /**
   * Update a material ticket
   */
  async updateMaterialTicket(
    jobId: string,
    ticketId: string,
    data: Partial<CreateMaterialTicketRequest>
  ): Promise<MaterialTicket> {
    const response = await apiClient.patch<{ ticket: MaterialTicket }>(
      `/jobs/${jobId}/tickets/${ticketId}`,
      data
    );
    return ensureMaterialTicketResponse(response).ticket;
  },

  /**
   * Delete a material ticket
   */
  async deleteMaterialTicket(jobId: string, ticketId: string): Promise<void> {
    await apiClient.delete(`/jobs/${jobId}/tickets/${ticketId}`);
  },
};
